import type { FirmClient } from '../../../generated/graphql';
import { EstateStatusItemState } from '../../../generated/graphql';
import type {
  EstateStatus,
  EstateStatusAttachmentLink,
} from '../../../EstateStatus/types';
import {
  ClientEstateStatusScoreHealthRange,
  DatePicker,
  ElementDetailViewHeader,
  ElementDetailViewIndexPageContainer,
  ElementsButton,
  Icon,
  SegmentedControlAnimated,
  SideOver,
  StandaloneScorecardElement,
  TextArea,
  Tooltip,
  TooltipContainer,
  openNotification,
} from '../../components';
import {
  ESTATE_STATUS_DETAIL_DATE_PICKER_OFFSET_YEARS,
  ESTATE_STATUS_DETAIL_EXPAND_ANIMATION_DURATION_SECONDS,
  EstateStatusAttachmentDetails,
  EstateStatusChecklistItemCard,
  formatEstateStatusWeight,
} from '../../../EstateStatus';
import { LocalThemeContextProvider } from '../../context';
import { elementsColors } from '../../../util';
import { Link, Outlet } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEstateStatusElementDetailViewIndexPage } from './hooks';

export interface EstateStatusElementDetailViewTrackingHandlers {
  onChecklistItemStateChanged?: (state: EstateStatusItemState) => void;
  onChecklistSaved?: (savedEstateStatus: EstateStatus) => void;
  onAttachmentUploaded?: () => void;
  onAttachmentRemoved?: () => void;
}

export interface EstateStatusElementDetailViewLayoutPageProps {
  baseRoute: string;
  exitRoute: string;
  children?: ReactNode;
}

export interface EstateStatusElementDetailViewIndexPageProps extends EstateStatusElementDetailViewTrackingHandlers {
  firmID: string;
  householdID: string;
  firmClient?: FirmClient | null;
  beneficiaryAccountsReviewRoute?: string;
}

export interface EstateStatusElementDetailViewPageProps
  extends EstateStatusElementDetailViewLayoutPageProps,
    EstateStatusElementDetailViewIndexPageProps {}

export function EstateStatusElementDetailViewLayoutPage({
  baseRoute,
  exitRoute,
  children,
}: EstateStatusElementDetailViewLayoutPageProps) {
  return (
    <SideOver baseRoute={baseRoute} exitRoute={exitRoute} className="!px-0">
      <LocalThemeContextProvider paletteSeedColor={elementsColors.Es}>
        {children ?? <Outlet />}
      </LocalThemeContextProvider>
    </SideOver>
  );
}

export function EstateStatusElementDetailViewPage({
  baseRoute,
  exitRoute,
  firmID,
  householdID,
  firmClient,
  beneficiaryAccountsReviewRoute,
  onChecklistItemStateChanged,
  onChecklistSaved,
  onAttachmentUploaded,
  onAttachmentRemoved,
}: EstateStatusElementDetailViewPageProps) {
  return (
    <EstateStatusElementDetailViewLayoutPage baseRoute={baseRoute} exitRoute={exitRoute}>
      <EstateStatusElementDetailViewIndexPage
        firmID={firmID}
        householdID={householdID}
        firmClient={firmClient}
        beneficiaryAccountsReviewRoute={beneficiaryAccountsReviewRoute}
        onChecklistItemStateChanged={onChecklistItemStateChanged}
        onChecklistSaved={onChecklistSaved}
        onAttachmentUploaded={onAttachmentUploaded}
        onAttachmentRemoved={onAttachmentRemoved}
      />
    </EstateStatusElementDetailViewLayoutPage>
  );
}

export function EstateStatusElementDetailViewIndexPage({
  firmID,
  householdID,
  firmClient,
  beneficiaryAccountsReviewRoute,
  onChecklistItemStateChanged,
  onChecklistSaved,
  onAttachmentUploaded,
  onAttachmentRemoved,
}: EstateStatusElementDetailViewIndexPageProps) {
  const { t } = useTranslation('estateStatus');
  const {
    allowTargetScoreRanges,
    checklist,
    completedLabel,
    estateStatusScorecardElementValue,
    estateStatusStates,
    excludedCount,
    expandedItemIDs,
    hasBlockingError,
    isEstateStatusDisabled,
    isLoadingEstateStatus,
    mathLabel,
    saveEstateStatusHouseholdSettingsMutation,
    stateLabels,
    summary,
    formatEstateStatusPercent,
    getItemDetailsLabel,
    mapUploadedFileToAttachmentLink,
    toggleExpandedItem,
    updateChecklistItem,
  } = useEstateStatusElementDetailViewIndexPage({ firmID, householdID, onChecklistSaved });

  return (
    <ElementDetailViewIndexPageContainer>
      <ElementDetailViewHeader
        title={t('title')}
        tooltipContainerClassName="md:!w-[312px]"
        tooltipDescription={t('header-tooltip-description')}
      />

      <div className="pb-10">
        {isEstateStatusDisabled ? (
          <p className="text-sm text-darkMode-gray-light/80">{t('disabled-message')}</p>
        ) : isLoadingEstateStatus ? (
          <p className="text-sm text-darkMode-gray-light/70">{t('loading-message')}</p>
        ) : hasBlockingError ? (
          <p className="text-sm text-red-200">{t('load-error-message')}</p>
        ) : (
          <div className="space-y-4">
            <div className="sm:mt-[43px] sm:flex">
              <StandaloneScorecardElement element="Es" value={estateStatusScorecardElementValue} />

              <div className="mt-3 flex max-w-[422px] flex-1 flex-col rounded-[10px] bg-darkMode-gray-medium/[0.16] px-6 pb-[30px] sm:ml-[19px] sm:mt-0">
                <div className="mb-2 mt-[6px] flex h-12 items-center justify-between text-base text-darkMode-gray-medium">
                  <p className="font-bold">{t('calculation-factors')}</p>
                  <Tooltip
                    offset={[0, 8]}
                    placement="top"
                    floatingElement={
                      <TooltipContainer className="w-[240px] !bg-darkMode-gray-floatingMenu/80">
                        <p className="text-xs !leading-[15px] text-white">{t('calculation-tooltip-description')}</p>
                      </TooltipContainer>
                    }
                  >
                    <Icon icon="infoOutline" className="text-palette-600" />
                  </Tooltip>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('checklist-progress')}
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-white">{completedLabel}</p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                        {t('earned-weight')}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-white">
                        {formatEstateStatusWeight(summary.earnedWeight)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                        {t('applicable-weight')}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-white">
                        {formatEstateStatusWeight(summary.applicableWeight)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                        {t('template-weight')}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-white">
                        {formatEstateStatusWeight(summary.totalWeight)}
                      </p>
                    </div>
                  </div>

                  {excludedCount > 0 ? (
                    <p className="text-sm text-darkMode-gray-light/70">
                      {t('excluded-label', {
                        count: excludedCount,
                        itemLabel: t(excludedCount === 1 ? 'item-singular' : 'item-plural'),
                      })}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {allowTargetScoreRanges && firmClient?.id && firmClient.firmID ? (
              <ClientEstateStatusScoreHealthRange
                className="mt-10"
                firmClientID={firmClient.id}
                firmID={firmClient.firmID}
              />
            ) : null}

            <div className="rounded-3xl border border-darkMode-gray-medium/30 bg-darkMode-gray-darkest/95 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium uppercase tracking-[0.2em] text-darkMode-gray-light/75">
                    {t('current-score')}
                  </p>
                  <p className="mt-2 text-4xl font-semibold text-white">{formatEstateStatusPercent(summary.score)}</p>
                  <p className="mt-2 text-sm text-darkMode-gray-light/75">{mathLabel}</p>
                </div>

                <div className="grid gap-3 text-right sm:grid-cols-3 sm:text-left">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('earned-weight')}
                    </p>
                    <p className="mt-1 text-xl font-semibold text-white">
                      {formatEstateStatusWeight(summary.earnedWeight)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('applicable-weight')}
                    </p>
                    <p className="mt-1 text-xl font-semibold text-white">
                      {formatEstateStatusWeight(summary.applicableWeight)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
                      {t('template-weight')}
                    </p>
                    <p className="mt-1 text-xl font-semibold text-white">
                      {formatEstateStatusWeight(summary.totalWeight)}
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-sm text-darkMode-gray-light/70">{t('current-score-description')}</p>
            </div>

            {checklist.map((item) => (
              <EstateStatusChecklistItemCard
                key={item.id}
                item={item}
                headerActionLabel={expandedItemIDs.includes(item.id) ? t('hide-details') : getItemDetailsLabel(item)}
                onHeaderAction={() => toggleExpandedItem(item.id)}
                topContent={
                  <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start">
                    <div className="min-w-0 overflow-hidden">
                      <SegmentedControlAnimated
                        className="w-max max-w-full"
                        activeItemIndex={estateStatusStates.indexOf(item.state)}
                        activeItemLayoutId={`EstateStatusSegmentedControlActive-${item.id}`}
                        items={estateStatusStates.map((state) => stateLabels[state])}
                        setItem={(stateIndex) => {
                          const selectedState = estateStatusStates[stateIndex];

                          if (selectedState) {
                            onChecklistItemStateChanged?.(selectedState);
                            updateChecklistItem(item.id, (currentItem) => ({
                              ...currentItem,
                              state: selectedState,
                              lastReviewedAt:
                                selectedState === EstateStatusItemState.Yes ? currentItem.lastReviewedAt ?? null : null,
                            }));
                          }
                        }}
                      />
                    </div>

                    <div className="min-h-0">
                      <AnimatePresence initial={false}>
                        {item.state === EstateStatusItemState.Yes ? (
                          <motion.div
                            key={`${item.id}-review-date`}
                            initial={{ height: 0, opacity: 0, y: -4 }}
                            animate={{ height: 'auto', opacity: 1, y: 0 }}
                            exit={{ height: 0, opacity: 0, y: -4 }}
                            transition={{
                              duration: ESTATE_STATUS_DETAIL_EXPAND_ANIMATION_DURATION_SECONDS,
                              ease: 'easeOut',
                            }}
                            className="overflow-hidden"
                          >
                            <DatePicker
                              className="w-full [&_.ElementsInput-label]:!text-darkMode-gray-light/80"
                              label={t('last-reviewed-label')}
                              offsetYears={ESTATE_STATUS_DETAIL_DATE_PICKER_OFFSET_YEARS}
                              placeholder={t('date-placeholder')}
                              placement="bottom-end"
                              status="normal"
                              value={item.lastReviewedAt ?? undefined}
                              inPortal
                              onChange={(dateISO) =>
                                updateChecklistItem(item.id, (currentItem) => ({
                                  ...currentItem,
                                  lastReviewedAt: dateISO ?? null,
                                }))
                              }
                            />
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </div>
                }
              >
                {expandedItemIDs.includes(item.id) ? (
                  <div className="space-y-4 border-t border-darkMode-gray-medium/20 pt-4">
                    {item.id === 'beneficiaries' && beneficiaryAccountsReviewRoute ? (
                      <Link className="contents" to={beneficiaryAccountsReviewRoute}>
                        <ElementsButton
                          type="button"
                          theme="secondary"
                          icon="chevronRight"
                          label={t('review-beneficiary-accounts')}
                        />
                      </Link>
                    ) : null}
                    <TextArea
                      autosize={false}
                      rows={3}
                      className="w-full"
                      label={t('note-label')}
                      status="normal"
                      value={item.note ?? ''}
                      textAreaProps={{ placeholder: t('note-placeholder') }}
                      onChange={(event) =>
                        updateChecklistItem(item.id, (currentItem) => ({
                          ...currentItem,
                          note: event.target.value,
                        }))
                      }
                    />

                    <div className="space-y-3">
                      <EstateStatusAttachmentDetails
                        attachmentLinks={item.attachmentLinks}
                        emptyState={t('attachments-empty')}
                        tenantID={householdID}
                        onFileCreation={(file) => {
                          const attachmentLink = mapUploadedFileToAttachmentLink(file);
                          const attachmentURL = attachmentLink.url?.trim() ?? '';
                          const attachmentFileName = attachmentLink.fileName?.trim() ?? '';

                          if (!attachmentURL || !attachmentFileName) {
                            openNotification({
                              type: 'error',
                              description: t('attachments-upload-error'),
                            });
                            return;
                          }

                          onAttachmentUploaded?.();
                          updateChecklistItem(item.id, (currentItem) => ({
                            ...currentItem,
                            attachmentLinks: [...currentItem.attachmentLinks, attachmentLink],
                          }));
                        }}
                        onRemoveAttachment={(_attachmentLink, attachmentIndex) => {
                          onAttachmentRemoved?.();
                          updateChecklistItem(item.id, (currentItem) => ({
                            ...currentItem,
                            attachmentLinks: currentItem.attachmentLinks.filter(
                              (_currentAttachmentLink: EstateStatusAttachmentLink, currentAttachmentIndex: number) =>
                                currentAttachmentIndex !== attachmentIndex
                            ),
                          }));
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </EstateStatusChecklistItemCard>
            ))}

            <div className="rounded-3xl border border-darkMode-gray-medium/30 bg-darkMode-gray-darkest/95 p-5">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-darkMode-gray-light/75">
                {t('how-this-adds-up')}
              </p>
              <p className="mt-3 text-lg font-medium text-white">{mathLabel}</p>
              <p className="mt-2 text-sm text-darkMode-gray-light/70">{t('how-this-adds-up-description')}</p>
            </div>

            <div className="flex items-start justify-end gap-3 pt-2">
              {saveEstateStatusHouseholdSettingsMutation.isError ? (
                <p className="mr-auto text-sm text-red-200">{t('save-error-message')}</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </ElementDetailViewIndexPageContainer>
  );
}
